/**
 * useScrollReveal — scroll-triggered entrance animation for a section.
 *
 * Adds the `.aces-reveal` class (src/styles/global.css), a CSS scroll-driven
 * animation. Content is never hidden by JS: in browsers without scroll
 * timelines, with reduced motion, in screenshots and for crawlers, the
 * section simply shows as-is.
 *
 * Usage:
 *   const ref = useScrollReveal<HTMLDivElement>();
 *   <div ref={ref}> ... </div>
 */
import { useEffect, useRef } from 'react';

interface Options {
  /** Kept for call-site compatibility; the CSS animation ignores it. */
  threshold?: number;
  rootMargin?: string;
  delay?: number;
}

export function useScrollReveal<T extends HTMLElement = HTMLElement>(
  _opts: Options = {},
): React.RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    ref.current?.classList.add('aces-reveal');
  }, []);

  return ref;
}
